import { useRef, useState } from "react";

// Past this fraction of the card's width, releasing fires the action.
const SWIPE_THRESHOLD = 0.4;
// Below this many px, treat as a tap (open detail), not a swipe.
const TAP_SLOP = 6;

export function SwipeableOrderCard({
  children,
  onSwipeRight,
  onSwipeLeft,
  onTap,
  disabled,        // fully inert — no tap, no swipe (e.g. action in flight)
  disableSwipe,    // tap still fires, swipe gesture is suppressed
  className = "",
  style,
}) {
  const cardRef = useRef(null);
  const startX = useRef(null);
  const startY = useRef(null);
  const widthRef = useRef(0);
  const movedRef = useRef(false);
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);

  const handlePointerDown = (e) => {
    if (disabled) return;
    // Ignore secondary buttons / multi-touch.
    if (e.button !== undefined && e.button !== 0) return;
    cardRef.current?.setPointerCapture?.(e.pointerId);
    startX.current = e.clientX;
    startY.current = e.clientY;
    widthRef.current = cardRef.current?.offsetWidth || 0;
    movedRef.current = false;
    setAnimating(false);
  };

  const handlePointerMove = (e) => {
    if (startX.current == null) return;
    const dxNow = e.clientX - startX.current;
    const dyNow = e.clientY - startY.current;
    // Lock to horizontal once the user clearly moves sideways. If the gesture
    // is mostly vertical, let the page scroll instead of capturing it.
    if (!movedRef.current) {
      if (Math.abs(dxNow) < TAP_SLOP && Math.abs(dyNow) < TAP_SLOP) return;
      // If swipe is suppressed (e.g. selection mode), any meaningful movement
      // ends the gesture so the page can scroll — but a still-pointer release
      // still counts as a tap.
      if (disableSwipe || Math.abs(dyNow) > Math.abs(dxNow)) {
        startX.current = null;
        return;
      }
      movedRef.current = true;
    }
    setDx(dxNow);
  };

  const finish = (action) => {
    const width = widthRef.current || 1;
    setAnimating(true);
    // Slide the card off-screen in the direction of the swipe before firing.
    setDx(action === "right" ? width * 1.2 : -width * 1.2);
    setTimeout(() => {
      if (action === "right") onSwipeRight?.();
      else if (action === "left") onSwipeLeft?.();
      // Reset for next render — the card itself unmounts after the action,
      // so this only matters if the parent keeps it mounted.
      setDx(0);
      setAnimating(false);
    }, 220);
  };

  const handlePointerUp = () => {
    if (startX.current == null) return;
    const wasMoved = movedRef.current;
    const finalDx = dx;
    startX.current = null;
    startY.current = null;

    if (!wasMoved) {
      // Tap.
      onTap?.();
      return;
    }

    const width = widthRef.current || 1;
    const ratio = Math.abs(finalDx) / width;
    if (ratio >= SWIPE_THRESHOLD) {
      finish(finalDx > 0 ? "right" : "left");
    } else {
      // Snap back.
      setAnimating(true);
      setDx(0);
      setTimeout(() => setAnimating(false), 220);
    }
  };

  const handlePointerCancel = () => {
    startX.current = null;
    startY.current = null;
    setAnimating(true);
    setDx(0);
    setTimeout(() => setAnimating(false), 220);
  };

  // Background hint shows the action that will fire on release.
  const intent = dx > 0 ? "approve" : dx < 0 ? "reject" : null;
  const width = widthRef.current || 1;
  const progress = Math.min(1, Math.abs(dx) / (width * SWIPE_THRESHOLD));

  return (
    <div className={`swipe-wrap ${className}`} style={style}>
      <div className={`swipe-bg swipe-bg-approve ${intent === "approve" ? "visible" : ""}`}>
        <span className="swipe-icon" style={{ opacity: progress }}>✓</span>
      </div>
      <div className={`swipe-bg swipe-bg-reject ${intent === "reject" ? "visible" : ""}`}>
        <span className="swipe-icon" style={{ opacity: progress }}>✗</span>
      </div>
      <div
        ref={cardRef}
        className="swipe-card"
        style={{
          transform: `translateX(${dx}px)`,
          transition: animating ? "transform 0.22s ease-out" : "none",
          touchAction: "pan-y",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        {children}
      </div>
    </div>
  );
}
